// src/pages/TicketLinkTypes/TicketLinkTypeList.jsx
// -----------------------------------------------------------------------------
// Admin screen for Ticket_Link_Type_TBL.
// Full CRUD operations built with TableList, TableForm, DeleteConfirmModal,
// and custom hook useTableData.
// -----------------------------------------------------------------------------
import { useMemo } from 'react';
import ticketLinkTypeService from '../../api/ticketLinkTypeService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function TicketLinkTypeList() {
  // Initialize CRUD Hook
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    onSearchChange, // Wait, in TableList it is called onSearchChange but useTableData returns setSearchTerm. Let's make sure we map it!
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketLinkTypeService, {
    idKey: 'linkTypeID',
    itemName: 'Ticket Link Type',
    initialSort: { key: 'linkTypeID', direction: 'desc' },
  });

  // Schema configuration for Ticket_Link_Type_TBL
  const linkTypeFields = useMemo(() => {
    const isEditMode = formMode === 'edit';
    const fields = [];

    if (isEditMode) {
      fields.push({
        name: 'linkTypeID',
        label: 'Link Type ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
    }

    fields.push({
      name: 'linkTypeName',
      label: 'Link Type Name',
      type: 'varchar',
      required: true,
      maxLength: 50,
      placeholder: 'e.g. Duplicate, Related, Parent-Child',
    });

    return fields;
  }, [formMode]);

  // Table Columns Definition
  const columns = [
    { key: 'linkTypeID', label: 'Link Type ID', sortable: true, width: '120px' },
    { key: 'linkTypeName', label: 'Link Type Name', sortable: true },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Link Types</h1>
          <div className="subtitle">
            Full CRUD settings for ticket-to-ticket relationship classifications.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Link Type
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="linkTypeID"
        emptyMessage="No link types found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={linkTypeFields}
          initialValues={selectedRecord || { linkTypeName: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Ticket Link Type"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Ticket Link Type"
        itemLabel={selectedRecord ? selectedRecord.linkTypeName : 'Link Type'}
        deleting={deleting}
      />
    </div>
  );
}
